// Admin EnrollmentsPage — paginated list of every enrollment, inline manual
// "Enroll student" form, and an inline payment-status update per row.
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useFetch, postJson } from "../../hooks/useApi.js";
import { useCourses } from "../../contexts/CoursesContext.js";
import AdminEnrollmentRow from "../../components/admin/AdminEnrollmentRow.js";
import type { AdminEnrollment } from "../../lib/adminEnrollmentTypes.js";
import type { AdminUserListItem, Enrollment, PaginatedResult } from "@dm-lms/shared/types";

const inputClass =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";

export default function EnrollmentsPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useFetch<PaginatedResult<AdminEnrollment>>(
    `/api/enrollments?page=${page}&pageSize=20`
  );
  const { data: studentsData } = useFetch<PaginatedResult<AdminUserListItem>>(
    "/api/users?role=student&page=1&pageSize=100"
  );
  const { courses } = useCourses();
  const publishedCourses = courses.filter((course) => course.isPublished);

  const [enrollments, setEnrollments] = useState<AdminEnrollment[]>([]);
  useEffect(() => {
    if (data) setEnrollments(data.items);
  }, [data]);

  const [showForm, setShowForm] = useState(false);
  const [studentId, setStudentId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  async function enrollStudent(event: FormEvent) {
    event.preventDefault();
    if (!studentId || !courseId) return;
    setCreateError(null);
    setCreating(true);

    const result = await postJson<Enrollment>("/api/enrollments", { studentId, courseId });

    setCreating(false);
    if (!result.ok) {
      setCreateError(result.error);
      return;
    }

    const student = studentsData?.items.find((s) => s.id === studentId);
    const course = courses.find((c) => c.id === courseId);
    setEnrollments((prev) => [
      {
        ...result.data,
        studentName: student?.name ?? "—",
        studentCode: student?.code ?? "—",
        courseTitle: course?.title ?? "—",
        courseCode:  course?.code ?? "—",
      },
      ...prev,
    ]);
    setStudentId("");
    setCourseId("");
  }

  return (
    <div className="dm-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-dm-navy">Enrollments</h1>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white hover:bg-dm-blue"
        >
          {showForm ? "Cancel" : "Enroll student"}
        </button>
      </div>

      {isLoading && <p className="text-dm-slate text-sm">Loading enrollments…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && data && (
        <>
          {enrollments.length === 0 ? (
            <p className="text-dm-slate text-sm">No enrollments yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-dm-slate">
                  <tr>
                    <th className="px-4 py-3 font-medium">Student</th>
                    <th className="px-4 py-3 font-medium">Course</th>
                    <th className="px-4 py-3 font-medium">Payment status</th>
                    <th className="px-4 py-3 font-medium">Enrolled</th>
                    <th className="px-4 py-3 font-medium">Completed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {enrollments.map((enrollment) => (
                    <AdminEnrollmentRow key={enrollment.id} enrollment={enrollment} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="text-xs text-dm-slate">
              Page {data.page} · {data.total} total
            </span>
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
              disabled={!data.hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {showForm && (
        <form onSubmit={enrollStudent} className="mt-6 max-w-lg rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-medium text-dm-navy">Enroll student</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dm-navy">Student</label>
              <select
                required
                value={studentId}
                onChange={(event) => setStudentId(event.target.value)}
                className={inputClass}
              >
                <option value="">Select…</option>
                {(studentsData?.items ?? []).map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name} ({student.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-dm-navy">Course</label>
              <select
                required
                value={courseId}
                onChange={(event) => setCourseId(event.target.value)}
                className={inputClass}
              >
                <option value="">Select…</option>
                {publishedCourses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title} ({course.code})
                  </option>
                ))}
              </select>
            </div>

            <p className="text-xs text-dm-slate">Payment status defaults to pending.</p>
          </div>

          {createError && <p className="mt-3 text-sm text-red-600">{createError}</p>}

          <div className="mt-4">
            <button
              type="submit"
              disabled={creating}
              className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
            >
              {creating ? "Enrolling…" : "Enroll student"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
